/**
 * Status display string → chip color class. Classes are defined in `themeNew.ts` global styles
 * (MuiCssBaseline). Frontend mapping — overrides whatever color the search configuration sends
 * from the backend.
 *
 * Lookup is case-insensitive; entries below use lowercase keys. Returning `undefined` lets MUI
 * render the default gray chip.
 */
export type StatusChipClass = 'status-chip-green' | 'status-chip-pink' | 'status-chip-red' | 'status-chip-blue';

const STATUS_TO_CLASS: Record<string, StatusChipClass> = {
    // Positive / complete
    approved: 'status-chip-green',
    delivered: 'status-chip-green',
    paid: 'status-chip-green',
    completed: 'status-chip-green',
    active: 'status-chip-green',
    closed: 'status-chip-green',
    sent: 'status-chip-green',
    fixed: 'status-chip-green',
    released: 'status-chip-green',
    'processing complete': 'status-chip-green',
    'workflow execution complete': 'status-chip-green',
    'customer status verified': 'status-chip-green',
    'vat compliance verified': 'status-chip-green',

    // In-flight / informational
    processing: 'status-chip-blue',
    draft: 'status-chip-blue',
    submitted: 'status-chip-blue',
    'in progress': 'status-chip-blue',
    new: 'status-chip-blue',
    inserted: 'status-chip-blue',
    'ready to process': 'status-chip-blue',
    'ready to dispatch': 'status-chip-blue',
    'vat processing': 'status-chip-blue',
    workflow: 'status-chip-blue',
    'determining customer': 'status-chip-blue',
    'transcepta is reviewing': 'status-chip-blue',
    research: 'status-chip-blue',

    // Waiting / hold
    parked: 'status-chip-pink',
    pending: 'status-chip-pink',
    hold: 'status-chip-pink',
    review: 'status-chip-pink',
    'buyer hold': 'status-chip-pink',
    'supplier hold': 'status-chip-pink',
    'holding for scheduled release': 'status-chip-pink',
    'holding for delivery address': 'status-chip-pink',
    'holding for delivery information': 'status-chip-pink',
    'holding for workflow': 'status-chip-pink',
    'holding for vat clearance': 'status-chip-pink',
    'holding for vat buyer response': 'status-chip-pink',
    'holding for duplicate vat invoice': 'status-chip-pink',
    'holding for google maps': 'status-chip-pink',

    // Negative / failure
    rejected: 'status-chip-red',
    'workflow rejected': 'status-chip-red',
    error: 'status-chip-red',
    failed: 'status-chip-red',
    cancelled: 'status-chip-red',
    canceled: 'status-chip-red',
    'canceled (unknown recipient)': 'status-chip-red',
    'partially canceled': 'status-chip-red',
    'canceled for partial reprocess': 'status-chip-red',
    'do not deliver': 'status-chip-red',
};

export const getStatusChipClass = (status: string | undefined): StatusChipClass | undefined => {
    if (!status) {
        return undefined;
    }
    return STATUS_TO_CLASS[status.toLowerCase()];
};
